import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Carousel.module.css';

interface CarouselProps {
  images?: string[];
  videoFile?: string;
  videoUrl?: string;
  alt?: string;
}

export function Carousel({ images = [], videoFile, videoUrl, alt = 'Project image' }: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Combine all media: video first, then images
  const allMedia: Array<{ type: 'image' | 'videoFile' | 'videoUrl'; src: string }> = [];

  if (videoUrl) {
    allMedia.push({ type: 'videoUrl', src: videoUrl });
  }

  if (videoFile) {
    allMedia.push({ type: 'videoFile', src: videoFile });
  }

  allMedia.push(...images.map((img) => ({ type: 'image' as const, src: img })));

  // If no media, don't render
  if (allMedia.length === 0) {
    return null;
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  const currentMedia = allMedia[currentIndex];

  return (
    <div className={styles.carousel}>
      <div className={styles.carouselInner}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            className={styles.carouselItem}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
          >
            {currentMedia.type === 'image' && (
              <img src={currentMedia.src} alt={`${alt} ${currentIndex + 1}`} className={styles.image} />
            )}

            {currentMedia.type === 'videoFile' && (
              <video
                src={currentMedia.src}
                controls
                className={styles.video}
                controlsList="nodownload"
              >
                Tu navegador no soporta el elemento de video.
              </video>
            )}

            {currentMedia.type === 'videoUrl' && (
              currentMedia.src.endsWith('.mp4') ? (
                <video
                  src={currentMedia.src}
                  controls
                  autoPlay
                  className={styles.video}
                  controlsList="nodownload"
                >
                  Tu navegador no soporta el elemento de video.
                </video>
              ) : (
                <div className={styles.videoWrapper}>
                  <iframe
                    src={`${currentMedia.src}?autoplay=1&mute=1`}
                    title={alt}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className={styles.iframe}
                  />
                </div>
              )
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        {allMedia.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className={`${styles.arrow} ${styles.arrowLeft}`}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              onClick={goToNext}
              className={`${styles.arrow} ${styles.arrowRight}`}
              aria-label="Next"
            >
              ›
            </button>
          </>
        )}
      </div>

      {/* Dots Indicator */}
      {allMedia.length > 1 && (
        <div className={styles.dots}>
          {allMedia.map((_, index) => (
            <button
              key={index}
              onClick={() => goToIndex(index)}
              className={`${styles.dot} ${index === currentIndex ? styles.dotActive : ''}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Counter */}
      {allMedia.length > 1 && (
        <div className={styles.counter}>
          {currentIndex + 1} / {allMedia.length}
        </div>
      )}
    </div>
  );
}
